import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Typography, Box, CircularProgress, Alert, Button, Paper, TextField
} from '@mui/material';
import apiService from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';

const ActivateAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // loading | success | error | resend | resend_success
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(location.state?.email || '');
  const justRegistered = location.state?.justRegistered || false;
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('resend');
      return;
    }

    const activate = async () => {
      try {
        const response = await apiService.get(`/auth/activate?token=${token}`);
        if (response.success) {
          setStatus('success');
          setMessage(response.message || 'Fiók sikeresen aktiválva!');
        } else {
          setStatus('error');
          setMessage(response.message || 'Fiók aktiválása sikertelen.');
        }
      } catch (err) {
        setStatus('error');
        setMessage(err.data?.message || err.message || 'Hiba történt a fiók aktiválása során.');
      }
    };

    activate();
  }, [searchParams]);

  const handleResend = async (e) => {
    e.preventDefault();
    setResendError('');

    if (!email) {
      setResendError('Az email cím megadása kötelező.');
      return;
    }

    setResendLoading(true);
    try {
      await apiService.post(API_ENDPOINTS.AUTH.RESEND_ACTIVATION, { email });
      setStatus('resend_success');
    } catch (err) {
      setResendError(err.data?.message || err.message || 'Hiba történt. Kérjük, próbálja újra.');
    } finally {
      setResendLoading(false);
    }
  };

  const resendForm = (
    <Box component="form" onSubmit={handleResend} sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Adja meg az email címét, és újraküldjük az aktiváló linket.
      </Typography>
      <TextField
        fullWidth
        label="Email cím"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={resendLoading}
        size="small"
        sx={{ mb: 2 }}
      />
      {resendError && (
        <Alert severity="error" sx={{ mb: 2 }}>{resendError}</Alert>
      )}
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={resendLoading}
      >
        {resendLoading ? 'Küldés...' : 'Aktiváló email újraküldése'}
      </Button>
    </Box>
  );

  return (
    <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: '12px' }}>

        {status === 'loading' && (
          <Box>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">Fiók aktiválása...</Typography>
          </Box>
        )}

        {status === 'success' && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>
            <Button variant="contained" onClick={() => navigate('/login')}>
              Bejelentkezés
            </Button>
          </Box>
        )}

        {status === 'error' && (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Új aktiváló email kérése
            </Typography>
            {resendForm}
            <Button variant="text" size="small" onClick={() => navigate('/')} sx={{ mt: 2 }}>
              Vissza a főoldalra
            </Button>
          </Box>
        )}

        {status === 'resend' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {justRegistered ? 'Sikeres regisztráció!' : 'Fiók aktiválása'}
            </Typography>
            <Alert severity={justRegistered ? 'success' : 'info'} sx={{ mb: 2, textAlign: 'left' }}>
              {justRegistered
                ? 'Köszönjük a regisztrációt! Ellenőrizze e-mail fiókját – küldtünk egy aktiváló linket. Kattintson rá a fiók aktiválásához.'
                : 'A fiók még nincs aktiválva. Kérjen új aktiváló emailt.'}
            </Alert>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {justRegistered ? 'Nem érkezett meg az email?' : 'Aktiváló email újraküldése'}
            </Typography>
            {resendForm}
          </Box>
        )}

        {status === 'resend_success' && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Ha a megadott e-mail cím regisztrált és még nem aktivált fiókhoz tartozik, hamarosan megérkezik az aktiváló link.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ellenőrizze a postaládáját (és a spam mappát is).
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/login')}>
              Vissza a bejelentkezéshez
            </Button>
          </Box>
        )}

      </Paper>
    </Container>
  );
};

export default ActivateAccount;
